import api from '@shared/api/apiConfig';
import { dpCompanyDetailsUri, dpPocDetailsUri, dpProfileDetailsUri, getAllDpProfileDetailsURI, getAllSpProfileDetailsURI, getDpConfigDetailsUri, getSpConfigDetailsUri, onboardDpUri, onboardSpUri, spCompanyDetailsUri, spPaymentDetailsUri, spPocDetailsUri, spProfileDetailsUri, updateDpProfileStatusUri, updateSpProfileStatusUri } from '@shared/api/endpoints';
import { dummyDemandPartners, dummySupplyPartners } from '@features/partnerManagement/data';


const supplyPartnerProfileStore = Object.fromEntries(
  dummySupplyPartners.map((partner) => [partner.spId, { ...partner }])
);

const demandPartnerProfileStore = Object.fromEntries(
  dummyDemandPartners.map((partner) => [partner.dpId, { ...partner }])
);

export interface BasePartnerProfile {
  status: string;
  createdAt: string;
  createdBy: string;
  updatedBy: string;
  updatedAt: string;
  pocCompleted: boolean;
  companyCompleted: boolean;
}

export interface SupplyPartnerProfile extends BasePartnerProfile {
  spId: string;
  spName: string;
  spSellerID: string;
  paymentCompleted: boolean;
}

export interface DemandPartnerProfile extends BasePartnerProfile {
  dpId: string;
  dpName: string;
  dpType: string;
  rtbVersion: string;
  configCompleted: boolean;
}

export interface SpPocDetails {
  spId?: string;
  spPocFirstName: string;
  spPocLastName: string;
  spPocEmailAddress: string;
  spPocMobileNumber: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface SpPaymentDetails {
  spId?: string;
  spBankName: string;
  spPaymentMethod: string;
  spPaymentCurrency: string;
  spPaymentDays: number;
  spBankAccountNumber: string;
  spBankAccountName: string;
  spBankRecipientType: string;
  spBankIfscCode: string;
  spBankPhoneNumber: string;
  spBankSwiftCode: string;
  spBankStreetAddress: string;
  spBankAddressLine2: string;
  spBankBranchName: string;
  spBankCityName: string;
  spBankPinCode: string;
  spBankAccountType: string;
  spBankCountry: string;
  spBankIbanNumber: string;
  spFinancePocFirstName: string;
  spFinancePocLastName: string;
  spFinancePocEmailAddress: string;
  spFinancePocMobileNumber: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface SpCompanyDetails {
  spId?: string;
  spCompanyName: string;
  spCompanyAddress: string;
  spCompanyCity: string;
  spCompanyState: string;
  spCompanyCountry: string;
  spCompanyZipCode: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface DpPocDetails {
  dpId?: string;
  dpPocFirstName: string;
  dpPocLastName: string;
  dpPocEmailAddress: string;
  dpPocMobileNumber: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface DpCompanyDetails {
  dpId?: string;
  dpCompanyName: string;
  dpCompanyAddress: string;
  dpCompanyCity: string;
  dpCompanyState: string;
  dpCompanyCountry: string;
  dpCompanyZipCode: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface SpConfigDetails {
  "appsList": Record<string, null>,
  "blockedDemandPartnerList": Record<string, null>,
  "rateLimitConfig": Record<string, number>,
}

export interface DpConfigDetails {
  "dpId"?: string
  "allowedCountryList": Record<string, null>,
  "allowedAppsList": Record<string, null>,
  "allowedFormats": Record<"banner"| "video"| "native",null>,
  "allowedPlatforms": Record<"android"| "ios"| "web", null>,
  "multiFormatSupport": boolean
}

export const supplyPartnerServices = {

  getAllSPprofileDetails(): Promise<SupplyPartnerProfile[]> {
    return api.get<any>(getAllSpProfileDetailsURI);

  },
  getSpProfileDetails(spId: string): Promise<SupplyPartnerProfile> {
    return api.get<any>(spProfileDetailsUri(spId));

  },
  getSpPocDetails(spId: string): Promise<SpPocDetails> {
    return api.get<any>(spPocDetailsUri(spId));

  },
  getSpCompanyDetails(spId: string): Promise<SpCompanyDetails> {
    return api.get<any>(spCompanyDetailsUri(spId));

  },
  getSpPaymentDetails(spId: string): Promise<SpPaymentDetails> {
    return api.get<SpPaymentDetails>(spPaymentDetailsUri(spId));

  },
  updateSpPocDetails(spId: string, data: SpPocDetails): Promise<any> {
    return api.put<any>(spPocDetailsUri(spId), data);

  },
  updateSpCompanyDetails(spId: string, data: SpCompanyDetails): Promise<any> {
    return api.put<any>(spCompanyDetailsUri(spId), data);

  },
  updateSpPaymentDetails(spId: string, data: SpPaymentDetails): Promise<any> {
    return api.put<any>(spPaymentDetailsUri(spId), data);

  },

  updateSpProfileStatus(spId: string, data: {status: string}): Promise<any> {
    return api.put<any>(updateSpProfileStatusUri(spId), data);
  },

  onboardSp(data:{spName: string}): Promise<any> {
    return api.post<any>(onboardSpUri, data);
  },
  
  getSpConfigDetails(spId: string): Promise<SpConfigDetails> { 
    return api.get<any>(getSpConfigDetailsUri(spId));
  },
  updateSpConfigDetails(spId: string, data: SpConfigDetails): Promise<any> {
    return api.put<any>(getSpConfigDetailsUri(spId), data);
  }

}

export const demandPartnerServices = {

  getAllDPprofileDetails(): Promise<DemandPartnerProfile[]> {
    return api.get<any>(getAllDpProfileDetailsURI);

  },
  getDPprofileDetails(dpId: string): Promise<DemandPartnerProfile> {
    return api.get<any>(dpProfileDetailsUri(dpId));

  },
  getDpPocDetails(dpId: string): Promise<DpPocDetails> {
    return api.get<any>(dpPocDetailsUri(dpId));

  },
  getDpCompanyDetails(dpId: string): Promise<DpCompanyDetails> {
    return api.get<any>(dpCompanyDetailsUri(dpId));

  },
  updateDpPocDetails(dpId: string, data: DpPocDetails): Promise<any> {
    return api.put<DpPocDetails>(dpPocDetailsUri(dpId), data);

  },
  updateDpCompanyDetails(dpId: string, data: DpCompanyDetails): Promise<any> {
    return api.put<DpCompanyDetails>(dpCompanyDetailsUri(dpId), data);

  },

  updateDpProfileStatus(dpId: string, data: {status: string}): Promise<any> {
    return api.put<any>(updateDpProfileStatusUri(dpId), data);
  },
  onboardDp(data:{dpName: string, dpType: string, rtbVersion: string}): Promise<any> {
    return api.post<any>(onboardDpUri, data);
  },
  getDpConfigDetails(dpId: string): Promise<DpConfigDetails> { 
    return api.get<any>(getDpConfigDetailsUri(dpId));
  },
  updateDpConfigDetails(dpId: string, data: DpConfigDetails): Promise<any> {
    return api.put<any>(getDpConfigDetailsUri(dpId), data);
  }

}

